<template>
  <v-col class="sign-in align-center justify-center">
    <v-row
      v-if="isLoading"
      class="loader"
    >
      <v-progress-circular
        :size="70"
        color="#055289"
        indeterminate
      />
    </v-row>

    <v-row
      v-else
      class="text-center darkestblue--text"
    >
      <v-col>
        <v-row>
          <v-col>
            <v-img
              class="ma-auto"
              src="../../assets/images/oao_logo_blue.png"
              alt="OAO"
              height="auto"
              max-width="60px"
            />
          </v-col>
        </v-row>

        <v-row class="white--text">
          <v-col>
            <v-form
              ref="form"
              @submit.prevent="attemptSignIn"
            >
              <v-col>
                <!--- Application Title -->
                <v-row class="sign-in-title display-1">
                  <v-col>
                    <span>iAdOps</span>
                  </v-col>
                </v-row>

                <!-- Description -->
                <v-row class="description">
                  <v-col>
                    <span>Numerous data sources.</span>

                    <br>

                    <span>One simple interface.</span>

                    <br>
                  </v-col>
                </v-row>

                <!-- Divider -->
                <v-row class="justify-center mt-4">
                  <v-col style="max-width: 320px">
                    <v-divider />
                  </v-col>
                </v-row>

                <!-- Sign-In Form -->
                <v-row>
                  <v-col>
                    <!-- Email -->
                    <v-row class="justify-center">
                      <v-col
                        style="max-width: 320px"
                        class="pb-0"
                      >
                        <v-text-field
                          v-model="inputEmail"
                          label="Work Email"
                          type="email"
                          class="d-block"
                          dark
                          dense
                          :disabled="isSigningIn"
                          hide-details
                          :loading="isSigningIn"
                          outlined
                          single-line
                          validate-on-blur
                          :rules="[
                            (v) => !!v || 'Email is required',
                            (v) => /.+@.+\..+/.test(v) || 'Email must be valid',
                          ]"
                          @keyup.enter="attemptSignIn"
                        >
                          <template
                            v-slot:append-outer
                            class="mt-0 ml-9"
                          >
                            <v-row>
                              <v-col class="pa-0 ml-2">
                                <v-checkbox
                                  v-model="rememberMe"
                                  label="Remember me"
                                  class="mt-0"
                                  color="white"
                                  dark
                                  dense
                                  outlined
                                >
                                  <template v-slot:label>
                                    <span class="caption mb-5">Remember me</span>
                                  </template>
                                </v-checkbox>
                              </v-col>
                            </v-row>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>

                    <!-- "Submit" Button -->
                    <v-row
                      v-if="showButtonToSubmitEmail"
                      class="justify-center mt-0"
                      transition="slide-y-transition"
                    >
                      <v-col>
                        <v-btn
                          :color="isSigningIn ? 'darkestblue' : 'white'"
                          :dark="isSigningIn"
                          :disabled="isSigningIn"
                          :loading="isSigningIn"
                          outlined
                          type="submit"
                          style="width: 290px"
                        >
                          <span class="caption">Submit</span>
                        </v-btn>
                      </v-col>
                    </v-row>

                    <!-- Provider Buttons -->
                    <v-row
                      v-if="showViewForProviderSelection"
                      :class="`justify-center ${haveProviderDocsExcludingPassword && showInputForPassword ? 'mt-3' : 'mt-0'}`"
                      transition="slide-y-transition"
                    >
                      <v-col class="pa-0">
                        <v-row
                          v-for="provider in providerDocsExcludingPassword"
                          :key="provider.providerId"
                        >
                          <v-col>
                            <v-btn
                              :color="isSigningIn ? 'darkestblue' : 'white'"
                              :dark="isSigningIn"
                              :disabled="isSigningIn"
                              :loading="isSigningIn"
                              width="290px"
                              @click="signInWithProvider(provider.providerId)"
                            >
                              <span class="caption">{{ provider.providerId === 'password' ? 'Sign In' : `Sign In with ${provider.providerName}` }}</span>
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>

                    <!-- "OR" Divider -->
                    <v-row
                      v-if="!isRegisteringAccount && haveProviderDocsExcludingPassword && showInputForPassword"
                      class="justify-center mt-3"
                      transition="slide-y-transition"
                    >
                      <v-col
                        class="pb-0"
                        style="max-width: 320px;"
                      >
                        <v-row class="align-center">
                          <v-col>
                            <v-divider class="darkestblue" />
                          </v-col>

                          <v-col>
                            <v-row>
                              <v-col>
                                <span class="caption font-weight-medium">OR</span>
                              </v-col>
                            </v-row>
                          </v-col>

                          <v-col>
                            <v-divider class="darkestblue" />
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>

                    <!-- "Enter Password" Input -->
                    <v-row
                      v-if="!isRegisteringAccount && showInputForPassword"
                      :class="`justify-center ${haveProviderDocsExcludingPassword ? 'mt-6' : 'mt-3'}`"
                      transition="slide-y-transition"
                    >
                      <v-col
                        style="max-width: 320px"
                        class="py-0"
                      >
                        <v-text-field
                          v-model="inputPassword"
                          :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                          label="Enter Password"
                          :type="showPassword ? 'text' : 'password'"
                          class="d-block"
                          color="white"
                          dark
                          dense
                          :disabled="isSigningIn"
                          hide-details
                          :loading="isSigningIn"
                          outlined
                          single-line
                          @keyup.enter="signInWithPassword"
                          @click:append="showPassword = !showPassword"
                        >
                          <template
                            v-slot:append-outer
                            class="mt-0 ml-9"
                          >
                            <v-row>
                              <v-col class="pa-0 ml-2">
                                <div
                                  class="caption text-right"
                                  style="width: 290px;"
                                >
                                  <span
                                    class="cursor-pointer"
                                    @click="promptToRequestResetPassword"
                                  >Reset Password</span>
                                </div>
                              </v-col>
                            </v-row>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>

                    <!-- "Sign In with Password" Button -->
                    <v-row
                      v-if="!isRegisteringAccount && showInputForPassword"
                      class="justify-center mt-3"
                      transition="slide-y-transition"
                    >
                      <v-col>
                        <v-btn
                          :color="isSigningIn ? 'darkestblue' : 'white'"
                          :dark="isSigningIn"
                          :disabled="isSigningIn"
                          :loading="isSigningIn"
                          style="width: 290px"
                          @click="signInWithPassword"
                        >
                          <span class="caption">Sign In with Password</span>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>

                <!-- Divider -->
                <v-row class="justify-center mt-4">
                  <v-col style="max-width: 320px">
                    <v-divider />
                  </v-col>
                </v-row>

                <!--- Footer -->
                <v-row class="justify-center mt-4">
                  <v-col style="max-width: 320px">
                    <v-row>
                      <!-- Register Account -->
                      <!-- <v-col
                        class="pt-0 pl-0"
                        style="max-width: 320px;"
                      >
                        <span
                          class="caption font-weight-medium cursor-pointer"
                          @click="promptToRegisterAccount"
                        >Register Account</span>
                      </v-col> -->

                      <!-- Privacy Policy Link -->
                      <v-col
                        class="pt-0 pl-0"
                        style="max-width: 320px;"
                      >
                        <a
                          class="caption font-weight-medium"
                          href="https://adops.com/privacy-policy/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >Privacy Policy</a>
                      </v-col>

                      <v-dialog
                        v-model="isRegisteringAccount"
                        width="420px"
                      >
                        <v-card>
                          <v-card-text>
                            <v-col>
                              <v-row>
                                <v-col class="pt-4">
                                  <h3 class="headline black--text">
                                    Register Account
                                  </h3>
                                </v-col>

                                <v-col class="col-auto mt-2">
                                  <v-btn
                                    :ripple="false"
                                    icon
                                    small
                                    @click.stop="cancelRegisterAccount"
                                  >
                                    <v-icon
                                      color="black"
                                      icon="close"
                                    >
                                      close
                                    </v-icon>
                                  </v-btn>
                                </v-col>
                              </v-row>

                              <!-- Register Account Form -->
                              <v-row>
                                <v-col>
                                  <v-text-field
                                    v-model="inputEmail"
                                    label="Work Email"
                                    type="email"
                                    dense
                                    hide-details
                                    outlined
                                    single-line
                                    validate-on-blur
                                    :rules="[
                                      (v) => !!v || 'Email is required',
                                      (v) => /.+@.+\..+/.test(v) || 'Email must be valid',
                                    ]"
                                  />
                                </v-col>
                              </v-row>

                              <!-- "Next" Button -->
                              <v-row v-if="!isRegisteringAccountWithProvider">
                                <v-col>
                                  <v-btn
                                    :ripple="false"
                                    color="darkestblue"
                                    dark
                                    outlined
                                    width="348px"
                                    @click="promptToRegisterWithProvider"
                                  >
                                    <span class="caption font-weight-medium">Next ...</span>
                                  </v-btn>
                                </v-col>
                              </v-row>

                              <!-- Provider Buttons -->
                              <v-row
                                v-if="isRegisteringAccountWithProvider"
                                class="justify-center mt-4"
                                transition="slide-y-transition"
                              >
                                <v-col class="pa-0">
                                  <v-row
                                    v-for="provider in providerDocsExcludingPassword"
                                    :key="provider.providerId"
                                    class="justify-center"
                                  >
                                    <v-col class="col-auto">
                                      <v-btn
                                        color="primary"
                                        :dark="isSigningIn"
                                        :disabled="isSigningIn"
                                        :loading="isSigningIn"
                                        width="350px"
                                        @click="registerAccount(provider.providerId)"
                                      >
                                        <span class="caption">{{ `Register with ${provider.providerName}` }}</span>
                                      </v-btn>
                                    </v-col>
                                  </v-row>
                                </v-col>
                              </v-row>

                              <!-- "OR" Divider -->
                              <v-row
                                v-if="isRegisteringAccountWithProvider && showInputToCreatePassword && haveProviderDocsExcludingPassword"
                                class="justify-center mt-3"
                                transition="slide-y-transition"
                              >
                                <v-col
                                  class="pb-0"
                                >
                                  <v-row class="align-center">
                                    <v-col>
                                      <v-divider class="darkestblue" />
                                    </v-col>

                                    <v-col class="col-auto">
                                      <v-row>
                                        <v-col>
                                          <span class="caption font-weight-medium">OR</span>
                                        </v-col>
                                      </v-row>
                                    </v-col>

                                    <v-col>
                                      <v-divider class="darkestblue" />
                                    </v-col>
                                  </v-row>
                                </v-col>
                              </v-row>

                              <!-- "Register Password" Input and Button  -->
                              <v-row
                                v-if="isRegisteringAccount && showInputToCreatePassword"
                                :class="`justify-center ${haveProviderDocsExcludingPassword ? 'mt-6' : 'mt-3'}`"
                                transition="slide-y-transition"
                              >
                                <v-col class="pa-0">
                                  <!-- "Register Password" Input -->
                                  <v-row class="justify-center mt-0">
                                    <v-col
                                      style="max-width: 350px"
                                      class="pt-0 px-0"
                                    >
                                      <v-text-field
                                        v-model="inputNewPassword"
                                        :append-icon="showNewPassword ? 'visibility' : 'visibility_off'"
                                        label="Enter Password"
                                        :type="showNewPassword ? 'text' : 'password'"
                                        dense
                                        :disabled="isSendingRegistration"
                                        hide-details
                                        outlined
                                        single-line
                                        validate-on-blur
                                        :rules="[
                                          () => passwordValidation.meetsAllCriteria || 'Criteria is required',
                                        ]"
                                        @keyup.enter="registerAccount('password')"
                                        @click:append="showNewPassword = !showNewPassword"
                                      />
                                    </v-col>
                                  </v-row>

                                  <!-- "Register Password" Button -->
                                  <v-row class="justify-center mt-1">
                                    <v-col class="col-auto">
                                      <v-btn
                                        color="primary"
                                        :disabled="isSendingPasswordRegistration"
                                        :loading="isSendingPasswordRegistration"
                                        style="width: 350px"
                                        @click="registerAccount('password')"
                                      >
                                        <span class="caption">{{ isSendingPasswordRegistration ? 'Registering ...' : 'Register with Password' }}</span>
                                      </v-btn>
                                    </v-col>
                                  </v-row>

                                  <!-- Success Message -->
                                  <v-row
                                    v-if="isRegistrationSuccess"
                                    class="justify-center ma-4 pa-4 rounded-lg icewhite"
                                  >
                                    <v-col class="pa-4">
                                      <span class="caption font-weight-medium">{{ successRegisteringAccount }}</span>
                                    </v-col>
                                  </v-row>

                                  <!-- Error Message -->
                                  <v-row
                                    v-if="didRegistrationFail"
                                    class="justify-center ma-4 pa-4 rounded-lg icewhite"
                                  >
                                    <v-col class="pa-4">
                                      <span class="caption font-weight-medium">{{ errorRegisteringAccount }}</span>
                                    </v-col>
                                  </v-row>

                                  <!-- "Validate Password" -->
                                  <v-row class="justify-center ma-4 pa-4 rounded-lg icewhite">
                                    <v-col class="pa-4">
                                      <v-row>
                                        <v-col>
                                          <span class="caption font-weight-bold">Your password must contain ...</span>
                                        </v-col>
                                      </v-row>

                                      <v-row class="mt-0">
                                        <v-col class="px-4 pb-4 pt-0">
                                          <v-row
                                            v-for="passwordRule in passwordRulesMap"
                                            :key="passwordRule.key"
                                            class="mt-0"
                                          >
                                            <v-col class="col-auto">
                                              <v-icon
                                                :color="passwordValidation[passwordRule.key] ? 'grassgreen' : 'error'"
                                                icon="check_circle"
                                                small
                                              >
                                                check_circle
                                              </v-icon>
                                            </v-col>

                                            <v-col class="col-auto pl-0">
                                              <span class="caption font-weight-medium">{{ passwordRule.text }}</span>
                                            </v-col>
                                          </v-row>
                                        </v-col>
                                      </v-row>
                                    </v-col>
                                  </v-row>
                                </v-col>
                              </v-row>
                            </v-col>
                          </v-card-text>
                        </v-card>
                      </v-dialog>

                      <!-- "Help" Button -->
                      <v-col class="pt-0 text-right">
                        <span
                          class="pl-1 caption font-weight-medium cursor-pointer"
                          @click="showHelpView = !showHelpView"
                        >Help</span>
                      </v-col>

                      <!-- "Help" Dialog -->
                      <v-dialog
                        v-model="showHelpView"
                        width="420px"
                      >
                        <v-card>
                          <v-card-text>
                            <v-col>
                              <v-row class="pt-4">
                                <v-col>
                                  <h3 class="headline">
                                    Help
                                  </h3>
                                </v-col>

                                <v-col class="col-auto mt-1">
                                  <v-btn
                                    :ripple="false"
                                    icon
                                    small
                                    @click="showHelpView = false"
                                  >
                                    <v-icon
                                      color="black"
                                      icon="close"
                                    >
                                      close
                                    </v-icon>
                                  </v-btn>
                                </v-col>
                              </v-row>

                              <v-row class="mt-0">
                                <v-col>
                                  <span class="font-weight-medium">
                                    Email our Help Desk at
                                    <a
                                      class="font-weight-bold"
                                      href="mailto:tech@adops.com"
                                    >tech@adops.com</a>.
                                  </span>
                                </v-col>
                              </v-row>

                              <v-row>
                                <v-col>
                                  <v-divider />
                                </v-col>
                              </v-row>

                              <v-row class="mt-0">
                                <v-col>
                                  <p class="mb-0 caption font-weight-medium">
                                    Release {{ version }}
                                  </p>

                                  <p class="mb-0 caption font-weight-medium">
                                    You're viewing the
                                    {{ tenantId && tenantId !== '[DEFAULT]' ? tenantId.split('-')[0] : 'default' }}
                                    instance for
                                    {{ domainName && domainName !== '[DEFAULT]' ? domainName : 'iAdOps' }}.
                                  </p>
                                </v-col>
                              </v-row>
                            </v-col>
                          </v-card-text>
                        </v-card>
                      </v-dialog>
                    </v-row>
                  </v-col>
                </v-row>
              </v-col>
            </v-form>
          </v-col>

          <!-- Auth Error Dialog -->
          <auth-error />

          <!-- "Request Reset Password" Dialog -->
          <v-dialog
            v-model="showViewToRequestResetPassword"
            width="420px"
          >
            <v-card>
              <v-card-text>
                <v-col>
                  <v-row class="pt-4">
                    <v-col class="pt-4">
                      <h3 class="headline black--text">
                        Reset Password
                      </h3>
                    </v-col>

                    <v-col class="col-auto mt-2">
                      <v-btn
                        :ripple="false"
                        icon
                        small
                        @click.stop="showViewToRequestResetPassword = false"
                      >
                        <v-icon
                          color="black"
                          icon="close"
                        >
                          close
                        </v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col>
                      <v-text-field
                        v-model="inputEmail"
                        label="Work Email"
                        type="email"
                        dense
                        hide-details
                        outlined
                        single-line
                        validate-on-blur
                        :rules="[
                          (v) => !!v || 'Email is required',
                          (v) => /.+@.+\..+/.test(v) || 'Email must be valid',
                        ]"
                      />
                    </v-col>
                  </v-row>

                  <v-row class="justify-center mt-3">
                    <v-col>
                      <v-btn
                        :ripple="false"
                        color="primary"
                        width="348px"
                        @click="sendResetPasswordEmail"
                      >
                        <span class="caption font-weight-medium">Email Password Reset Link</span>
                      </v-btn>
                    </v-col>
                  </v-row>

                  <v-row
                    v-if="haveSentPasswordResetEmail"
                    class="justify-center ma-4 pa-4 rounded-lg icewhite"
                  >
                    <v-col class="pa-4">
                      <span class="caption">Success! Check your email for the password reset link.</span>
                    </v-col>
                  </v-row>

                  <v-row
                    v-if="didSendPasswordResetEmailFail"
                    class="justify-center ma-4 pa-4 rounded-lg icewhite"
                  >
                    <v-col class="pa-4">
                      <span class="caption">{{ errorSendingPasswordResetEmail }}</span>
                    </v-col>
                  </v-row>
                </v-col>
              </v-card-text>
            </v-card>
          </v-dialog>

          <!-- "Reset Password" Dialog -->
          <v-dialog
            v-model="showViewToResetPassword"
            width="420px"
          >
            <v-card>
              <v-card-text>
                <v-col>
                  <v-row>
                    <v-col class="pt-4">
                      <h3 class="headline black--text">
                        Reset Password
                      </h3>
                    </v-col>

                    <v-col class="col-auto mt-2">
                      <v-btn
                        :ripple="false"
                        icon
                        small
                        @click.stop="cancelResetPassword"
                      >
                        <v-icon
                          color="black"
                          icon="close"
                        >
                          close
                        </v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>

                  <!-- "Reset Password" Input and Button  -->
                  <v-row
                    class="`justify-center mt-3"
                    transition="slide-y-transition"
                  >
                    <v-col class="pa-0">
                      <!-- "Reset Password" Input -->
                      <v-row class="justify-center mt-0">
                        <v-col
                          style="max-width: 350px"
                          class="pt-0 px-0"
                        >
                          <v-text-field
                            v-model="inputResetPassword"
                            :append-icon="showResetPassword ? 'visibility' : 'visibility_off'"
                            label="Enter Password"
                            :type="showResetPassword ? 'text' : 'password'"
                            dense
                            :disabled="isResettingPassword"
                            hide-details
                            outlined
                            single-line
                            validate-on-blur
                            :rules="[
                              () => passwordResetValidation.meetsAllCriteria || 'Criteria is required',
                            ]"
                            @keyup.enter="resetPassword"
                            @click:append="showResetPassword = !showResetPassword"
                          />
                        </v-col>
                      </v-row>

                      <!-- "Reset Password" Button -->
                      <v-row class="justify-center mt-1">
                        <v-col class="col-auto">
                          <v-btn
                            color="primary"
                            :disabled="isResettingPassword"
                            :loading="isResettingPassword"
                            style="width: 350px"
                            @click="resetPassword"
                          >
                            <span class="caption">{{ isResettingPassword ? 'Resetting password ...' : 'Reset Password' }}</span>
                          </v-btn>
                        </v-col>
                      </v-row>

                      <!-- "Reset Password" Success Message -->
                      <v-row
                        v-if="isResettingPasswordSuccess"
                        class="justify-center ma-4 pa-4 rounded-lg icewhite"
                      >
                        <v-col class="pa-4">
                          <span class="caption font-weight-medium">{{ successResettingAccount }}</span>
                        </v-col>
                      </v-row>

                      <!-- "Reset Password" Error Message -->
                      <v-row
                        v-if="didResetPasswordFail"
                        class="justify-center ma-4 pa-4 rounded-lg icewhite"
                      >
                        <v-col class="pa-4">
                          <span class="caption font-weight-medium">{{ errorResettingAccount }}</span>
                        </v-col>
                      </v-row>

                      <!-- "Validate Reset Password" -->
                      <v-row class="justify-center ma-4 pa-4 rounded-lg icewhite">
                        <v-col class="pa-4">
                          <v-row>
                            <v-col>
                              <span class="caption font-weight-bold">Your password must contain ...</span>
                            </v-col>
                          </v-row>

                          <v-row class="mt-0">
                            <v-col class="px-4 pb-4 pt-0">
                              <v-row
                                v-for="passwordRule in passwordRulesMap"
                                :key="passwordRule.key"
                                class="mt-0"
                              >
                                <v-col class="col-auto">
                                  <v-icon
                                    :color="passwordResetValidation[passwordRule.key] ? 'grassgreen' : 'error'"
                                    icon="check_circle"
                                    small
                                  >
                                    check_circle
                                  </v-icon>
                                </v-col>

                                <v-col class="col-auto pl-0">
                                  <span class="caption font-weight-medium">{{ passwordRule.text }}</span>
                                </v-col>
                              </v-row>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-col>
              </v-card-text>
            </v-card>
          </v-dialog>
        </v-row>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import { fireauth } from '@/store/firebase.js'
import {
  FIREBASE_DOMAIN_NAME,
  FIREBASE_DOMAIN_NAME_FOR_ADOPS_TENANT,
  FIREBASE_TENANT_ID,
  FIREBASE_TENANT_ID_FOR_ADOPS_TENANT,
} from '@/store/firebase'

const { mapState: mapAuthState, mapActions } = createNamespacedHelpers('auth')
const { mapState: mapLayoutState } = createNamespacedHelpers('layout')

export default {
  name: 'SignIn',
  components: {
    AuthError: () => import('../components/AuthError'),
  },
  data() {
    return {
      domainName: FIREBASE_DOMAIN_NAME,
      tenantId: FIREBASE_TENANT_ID,

      inputNewPassword: '',
      inputNewProvider: '',

      didSendPasswordResetEmailFail: false,
      errorSendingPasswordResetEmail: '',
      haveSentPasswordResetEmail: false,

      isRegisteringAccount: false,
      isRegisteringAccountWithProvider: false,
      isSendingRegistration: false,

      didRegistrationFail: false,
      isRegistrationSuccess: false,

      errorRegisteringAccount: '',
      successRegisteringAccount: 'Success! Check your email for the verification link.',

      inputResetPassword: '',
      showResetPassword: false,
      showInputToResetPassword: true,

      showViewToRequestResetPassword: false,
      showViewToResetPassword: false,
      isResettingPassword: false,

      didResetPasswordFail: false,
      isResettingPasswordSuccess: false,

      errorResettingAccount: '',
      successResettingAccount: 'Success! Sign in with your new password.',

      passwordRulesMap: [
        { key: 'meetsMinLength', text: 'Minimum of 8 characters' },
        { key: 'meetsLowerCase', text: 'Lowercase letter' },
        { key: 'meetsUpperCase', text: 'Uppercase letter' },
        { key: 'meetsNumber', text: 'Number' },
        { key: 'meetsSpecialChar', text: 'Special character' },
      ],
      passwordValidation: {
        meetsAllCriteria: false,
        meetsLowerCase: false,
        meetsMinLength: false,
        meetsNumber: false,
        meetsSpecialChar: false,
        meetsUpperCase: false,
      },
      passwordResetValidation: {
        meetsAllCriteria: false,
        meetsLowerCase: false,
        meetsMinLength: false,
        meetsNumber: false,
        meetsSpecialChar: false,
        meetsUpperCase: false,
      },

      showViewForProviderSelection: false,
      showButtonToSubmitEmail: true,
      showButtonToRegisterNextStep: true,
      showInputForPassword: false,
      showInputToCreatePassword: false,
      showPassword: false,
      showNewPassword: false,
    }
  },
  computed: {
    ...mapLayoutState({
      version: (state) => state.version,
    }),
    ...mapAuthState({
      user: (state) => state.user,
      userEmail: (state) => state.userEmail,
      userPassword: (state) => state.userPassword,

      $domainDoc: (state) => state.domainDoc,
      $providerDoc: (state) => state.providerDoc,
      $providerId: (state) => state.providerId,
      $providerDocs: (state) => state.providerDocs,

      isAutoAuthing: (state) => state.isAutoAuthing,
      isAuthCheckRequested: (state) => state.isAuthCheckRequested,
      isShowingHelp: (state) => state.isShowingHelp,
      isSigningIn: (state) => state.isSigningIn,
      didSignInFail: (state) => state.didSignInFail,
    }),

    domainDoc: {
      get() {
        return this.$domainDoc
      },
      set(value) {
        this.$store.commit('auth/domainDoc', value)
      },
    },
    providerDoc: {
      get() {
        return this.$providerDoc
      },
      set(value) {
        this.$store.commit('auth/providerDoc', value)
      },
    },
    providerId: {
      get() {
        return this.$providerId
      },
      set(value) {
        this.$store.commit('auth/providerId', value)
      },
    },
    providerDocs: {
      get() {
        return this.$providerDocs
      },
      set(value) {
        this.$store.commit('auth/providerDocs', value)
      },
    },

    showHelpView: {
      get() {
        return this.isShowingHelp
      },
      set(value) {
        this.$store.commit('auth/isShowingHelp', value)
      },
    },
    isLoading() {
      const { isAutoAuthing } = this

      return isAutoAuthing
    },
    inputEmail: {
      get() {
        const emailInQuery = this.$route.query.email
        return this.userEmail || emailInQuery || ''
      },
      set(value) {
        this.setUserEmail(value)
      },
    },
    inputPassword: {
      get() {
        return this.userPassword
      },
      set(value) {
        this.setUserPassword(value)
      },
    },
    rememberMe: {
      get() {
        return !!localStorage.getItem('workEmail')
      },
      set(value) {
        if (value) {
          localStorage.setItem('workEmail', this.inputEmail)
        } else {
          localStorage.removeItem('workEmail')
        }
      },
    },
    meetsAllCriteria() {
      return this.passwordValidation.meetsAllCriteria
    },
    activeProviderDocs() {
      return this.providerDocs.filter((provider) => provider.isActive)
    },
    passwordProviderDoc() {
      return this.activeProviderDocs.find((provider) => provider.providerId === 'password')
    },
    providerDocsExcludingPassword() {
      return this.activeProviderDocs.filter((provider) => provider.providerId !== 'password')
    },
    haveProviderDocsExcludingPassword() {
      return this.providerDocsExcludingPassword.length > 0
    },
    isSendingPasswordRegistration() {
      return this.isSendingRegistration && this.providerId === 'password'
    },
  },
  watch: {
    inputNewPassword(newValue) {
      this.passwordValidation = this.validatePassword(newValue)
    },
    inputResetPassword(newValue) {
      this.passwordResetValidation = this.validatePassword(newValue)
    },
    $route: {
      handler(newRoute, oldRoute) {
        const newRouteName = newRoute.name
        const oldRouteName = oldRoute?.name

        if (newRouteName === 'sign-up' && oldRouteName !== 'sign-up') {
          if (!this.isRegisteringAccount) {
            const { email } = this.$route.query

            // If the email is provided in the query, set it as the input email.
            // However, if the email is already set via rememberMe, do not overwrite it.
            if (email && !this.inputEmail) {
              this.inputEmail = email
            }

            // Open the dialog that allows the user to register an account.
            this.promptToRegisterAccount()
          }
        } else if (newRouteName === 'reset-password' && oldRouteName !== 'reset-password') {
          if (!this.showViewToRequestResetPassword) {
            // Open the dialog that allows the user to reset their password.
            this.promptToResetPassword()
          }
        }
      },
      immediate: true,
    },
  },
  created() {
    const url = window.location.href
    const isSignInWithEmailLink = fireauth.isSignInWithEmailLink(url)

    if (isSignInWithEmailLink) {
      this.signInWithEmailLink(url)
    }
  },
  methods: {
    ...mapActions([
      'createUserWithEmailAndPassword',
      'getProviderFromFirestore',
      'getHelp',
      'handleAuthError',
      'handleSignInError',
      'hideHelp',
      'sendEmailVerification',
      'sendSignInLinkToEmail',
      'setUserEmail',
      'setUserPassword',
      'signIn',
      'signInWithLink',
      'updatePassword',
    ]),

    validatePassword(password) {
      // Password could not be validated. Use the status to show what
      // requirements are met and which are missing.

      // The following requirements are available:
      // Lowercase character required
      // Uppercase character required
      // Numeric character required
      // Non-alphanumeric character required
      // The following characters satisfy the non-alphanumeric character requirement: ^ $ * . [ ] { } ( ) ? " ! @ # % & / \ , > < ' : ; | _ ~
      // Minimum password length (8 characters)
      // Maximum password length (100 characters)

      // If a criterion is undefined, it is not required by policy. If the
      // criterion is defined but false, it is required but not fulfilled by
      // the given password. For example:
      // { meetsLowerCase: true, meetsUpperCase: false, ... }

      const meetsLowerCase = password.match(/[a-z]/)
      const meetsUpperCase = password.match(/[A-Z]/)
      const meetsNumber = password.match(/[0-9]/)
      const meetsSpecialChar = password.match(/[^a-zA-Z0-9]/)
      const meetsMinLength = password.length >= 8
      const meetsMaxLength = password.length <= 100

      const meetsAllCriteria = meetsLowerCase && meetsUpperCase && meetsNumber && meetsSpecialChar && meetsMinLength && meetsMaxLength

      return {
        meetsAllCriteria,
        meetsLowerCase,
        meetsUpperCase,
        meetsNumber,
        meetsSpecialChar,
        meetsMinLength,
        meetsMaxLength,
      }
    },

    async showPasswordInput() {
      this.showInputForPassword = true
    },

    hidePasswordInput() {
      this.showInputForPassword = false
    },

    async showRegisterPasswordInput() {
      this.showInputToCreatePassword = true
    },

    hideRegisterPasswordInput() {
      this.showInputToCreatePassword = false
    },

    promptToRegisterAccount() {
      // Open a dialog that allows the user to create an account.
      this.isRegisteringAccount = true
    },

    promptToRequestResetPassword() {
      // Open a dialog that allows the user to reset their password.
      this.showViewToRequestResetPassword = true
    },

    async sendResetPasswordEmail() {
      // Check if the email is provided.
      if (!this.inputEmail) {
        // Alert the user that the email is required.
        return this.handleAuthError({ code: '403', layer: 'app', message: 'Email is required' })
      }

      // Construct the URL for the password reset email.
      const email = this.inputEmail
      const { tenantId } = this
      const url = `${window.location.origin}/reset-password?email=${email}`

      // Reset the user's password.
      return this.sendSignInLinkToEmail({ email, tenantId, url }).then((res) => {
        if (!res) {
          // Inform the user of the error.
          return this.handleAuthError({ code: '403', layer: 'app', message: 'Error Sending Email for Password Reset', status: 'error' })
        }

        if (res.error) {
          // Inform the user of the error.
          return this.handleAuthError(res)
        }

        // Inform the user that the password reset email was sent.
        this.haveSentPasswordResetEmail = true

        return res
      }).catch((error) => {
        // Set the error message for the "Reset Password" view to display.
        this.errorSendingPasswordResetEmail = error.message || 'Error sending password reset email. If the problem persists, please contact support.'

        // Render the error message in the "Reset Password" view.
        this.didSendPasswordResetEmailFail = true
      })
    },

    cancelResetPassword() {
      // Close the dialog that allows the user to reset their password.
      this.showViewToResetPassword = false

      // Reset the password input.
      this.inputResetPassword = ''
      this.passwordResetValidation = {
        meetsAllCriteria: false,
        meetsLowerCase: false,
        meetsMinLength: false,
        meetsNumber: false,
        meetsSpecialChar: false,
        meetsUpperCase: false,
      }

      // Reset the view's state.
      this.isResettingPassword = false
      this.didResetPasswordFail = false
      this.isResettingPasswordSuccess = false
    },

    promptToResetPassword() {
      // Open a dialog that allows the user to reset their password.
      this.showViewToResetPassword = true
    },

    async resetPassword() {
      // Reset the view's state.
      this.isResettingPassword = false
      this.didResetPasswordFail = false
      this.isResettingPasswordSuccess = false

      // Check if the email is provided.
      if (!this.inputEmail) {
        // Alert the user that the email is required.
        return this.handleAuthError({ code: '403', layer: 'app', message: 'Email is required' })
      }

      // Construct the URL for the password reset email.
      const { tenantId } = this

      const email = this.inputEmail
      const password = this.inputResetPassword
      const url = window.location.href

      // Reset the user's password.
      const res = await this.updatePassword({ email, password, tenantId, url }).catch((error) => {
        // Set the error message for the "Reset Password" view to display.
        this.errorRegisteringAccount = error.message || 'Error resetting password. If the problem persists, please contact support.'

        // Render the error message in the "Reset Password" view.
        this.didResetPasswordFail = true

        return error
      })

      if (!res) {
        // Inform the user of the error.
        return this.handleAuthError({ code: '403', layer: 'app', message: 'Error Resetting Password', status: 'error' })
      }

      if (res.error) {
        // Inform the user of the error.
        return this.handleAuthError(res)
      }

      // Inform the user that the password was reset.
      this.isResettingPasswordSuccess = true

      // Close the dialog that allows the user to reset their password.
      this.cancelResetPassword()

      // Update the URL hash to show the success message.
      return this.$router.push({ hash: '#success' })
    },

    async signInWithEmailLink(url) {
      const { name } = this.$route
      const { email } = this.$route.query

      // If the user is resetting their password, show the "Reset Password" view.
      if (name === 'reset-password') {
        return this.promptToResetPassword()
      }

      const { tenantId } = this

      // Sign in the user with a link.
      const res = await this.signInWithLink({ email, tenantId, url }).catch((error) => {
        const isMadeError = error && error.error
        return isMadeError ? error : { error: true, message: error?.message || 'Error signing in with email link' }
      })

      if (!res) {
        // Inform the user of the error.
        return this.handleSignInError({ code: '403', layer: 'app', message: 'Sign In Error', status: 'error' })
      }

      if (res.error) {
        // Inform the user of the error.
        return this.handleSignInError(res)
      }

      return res
    },

    async showProviderSelectionView(domainDoc, activeProviders) {
      // Get the domain's providers.
      const providerPromises = activeProviders.map((item) => this.getProviderFromFirestore({ domainDoc, providerId: item.providerId }).catch((error) => {
        // Inform the user and keep going so they can select from the available providers.
        this.handleAuthError(error)

        throw error
      }))

      // Remove any providers that failed to load.
      this.providerDocs = (await Promise.all(providerPromises)).filter((item) => item)

      if (this.providerDocsExcludingPassword.length > 0) {
        // Show the provider selection view.
        this.showViewForProviderSelection = true
      }

      // If the password provider is among the providers, reveal the password input to the user.
      if (this.passwordProviderDoc) {
        // Reveal the password input to the user.
        this.showPasswordInput()
      }
    },

    async showProviderRegistrationView(domainDoc, activeDomainProviders) {
      // Get the domain's providers.
      const providerPromises = activeDomainProviders.map((item) => this.getProviderFromFirestore({ domainDoc, providerId: item.providerId }).catch((error) => {
        // Inform the user and keep going so they can select from the available providers.
        this.handleAuthError(error)

        return null
      }))

      // Remove any providers that failed to load.
      this.providerDocs = (await Promise.all(providerPromises)).filter((item) => item)

      // If the password provider is among the providers, reveal the password input to the user.
      if (this.passwordProviderDoc) {
        // Reveal the password input to the user.
        this.showRegisterPasswordInput()
      }

      if (this.providerDocsExcludingPassword.length > 0) {
        // Show the provider selection dialog.
        this.isRegisteringAccountWithProvider = true
      }
    },

    cancelProviderRegistration(error) {
      // Asynchronously alert the user that the email is required.
      const result = error ? this.handleAuthError(error) : { error: false }

      // Hide the view that allows the user to register with a provider.
      // Hide the "Register Password" input.
      // Restore the "Register Account" form's "Next" button.

      // Hide the view that allows the user to sign-in with a provider.
      // Hide the "Enter Password" input.
      // Restore the "Sign In" form's "Submit" button.

      // Update the "Register Account" form.
      this.isRegistrationSuccess = false
      this.didRegistrationFail = false
      this.isRegisteringAccountWithProvider = false
      this.hideRegisterPasswordInput()
      this.showButtonToRegisterNextStep = true

      // Update the "Sign In" form.
      this.showViewForProviderSelection = false
      this.hidePasswordInput()
      this.showButtonToSubmitEmail = true

      return result
    },

    cancelRegisterAccount() {
      // Clear the provider selection dialog.
      this.cancelProviderRegistration()

      // Close the dialog that allows the user to register an account.
      this.isRegisteringAccount = false
    },

    async registerAccount(providerId) {
      // Reset state.
      this.isRegistrationSuccess = false
      this.didRegistrationFail = false
      this.isSendingRegistration = true

      // Check if the email is provided.
      if (!this.inputEmail) {
        this.isSendingRegistration = false

        // Alert the user that the email is required.
        return this.handleAuthError({ code: '403', layer: 'app', message: 'Email is required' })
      }

      // Store the providerId for reference by the view.
      this.providerId = providerId

      // Check if the user's domain is active.
      const { domainDoc } = this

      if (!domainDoc || domainDoc.error) {
        const defaultError = { code: '403', layer: 'app', message: 'Domain Error' }
        const error = domainDoc?.error || defaultError

        // Reset the registration state.
        return this.cancelProviderRegistration(error)
      }

      const providerDoc = await this.getProviderFromFirestore({ domainDoc, providerId }).catch((error) => {
        // Inform the user and keep going so they can select from the available providers.
        this.handleAuthError(error)

        return null
      })

      // Gather the user data.
      const registrationData = {
        email: this.inputEmail,
        password: this.inputNewPassword,
        provider: providerDoc,
        tenantId: this.tenantId,
      }

      if (providerId === 'password') {
        // Check if the password is provided.
        if (!this.inputNewPassword) {
          // Alert the user that the password is required.
          return this.handleAuthError({ code: '403', layer: 'app', message: 'Password is required' })
        }

        const result = await this.createUserWithEmailAndPassword(registrationData).catch((error) => {
          this.isSendingRegistration = false

          return this.handleAuthError(error)
        })

        const haveError = result && result.error

        if (haveError) {
          // Set the error message.
          this.errorRegisteringAccount = result.message || 'Error registering account. If the problem persists, please contact support.'

          // Reveal the error message.
          this.isRegistrationSuccess = false
          this.didRegistrationFail = true
        } else {
          // Get the URL for the verification email.
          const url = `${window.location.origin}/sign-up?email=${this.inputEmail}`

          // Send the user a verification email.
          this.sendEmailVerification(url).then(() => {
            // Log the success.
            // @todo Log the request to send a verification email.

            // Set the success message.
            this.successRegisteringAccount = 'Success! Check your email for the verification link.'

            // Reveal the success message.
            this.didRegistrationFail = false
            this.isRegistrationSuccess = true
          }).catch((error) => {
            // Get a logger that will output to Sentry.
            const logger = console

            // Alert developers of the error.
            logger.error('Error sending verification email:', error)

            // Set the error message.
            this.errorRegisteringAccount = 'Error sending verification email. If the problem persists, please contact support.'

            // Reveal the error message.
            this.isRegistrationSuccess = false
            this.didRegistrationFail = true
          })
        }

        // Reset the registration state.
        this.isSendingRegistration = false

        // Exit.
        return result
      }

      // Register the user with a provider other than password.
      // Sign in the user with a provider other than password.
      return this.signIn(registrationData).then((res) => {
        this.isSendingRegistration = false

        if (!res) {
          // Inform the user of the error.
          return this.handleAuthError({ code: '403', layer: 'app', message: 'Registration Error', status: 'error' })
        }

        if (res.error) {
          // Inform the user of the error.
          return this.handleAuthError(res)
        }

        return res
      }).catch((error) => {
        // Inform the user of the error.
        this.handleAuthError(error)
      })
    },

    async promptToRegisterWithProvider() {
      this.isRegisteringAccountWithProvider = true

      // Check if the email is provided.
      if (!this.inputEmail) {
        // Reset the registration state.
        return this.cancelProviderRegistration({ code: '403', layer: 'app', message: 'Email is required' })
      }

      const domainName = this.inputEmail.split('@')[1]

      // Check if the user's domain is active.
      // eslint-disable-next-line arrow-body-style
      const domainDoc = await this.$store.dispatch('auth/getDomainDocFromFirestore', domainName).catch((error) => {
        // Reset the registration state.
        return this.cancelProviderRegistration(error)
      })

      if (!domainDoc || domainDoc.error) {
        const defaultError = { code: '403', layer: 'app', message: 'Domain Error' }
        const error = domainDoc?.error || defaultError

        // Reset the registration state.
        return this.cancelProviderRegistration(error)
      }

      // Handle inactive domains.
      if (!domainDoc.isActive) {
        const message = 'Domain Inactive'
        const error = { code: '403', layer: 'app', message }

        // Reset the registration state.
        return this.cancelProviderRegistration(error)
      }

      // Save the domain document for later use by the provider selection dialog.
      this.domainDoc = domainDoc

      // Determine whether the user's domain is misconfigured.
      if (!Array.isArray(domainDoc.providers)) {
        const message = 'Domain Misconfiguration Error: Providers Not Found'
        const error = { code: '403', layer: 'app', message }

        return this.cancelProviderRegistration(error)
      }

      // Determine whether the user's domain has multiple providers enabled.
      const domainProviders = domainDoc.providers
      const activeDomainProviders = domainProviders.filter((provider) => provider.isActive)

      // Determine whether the user's domain has any providers enabled.
      if (activeDomainProviders.length === 0) {
        // Inform the user and keep going so they can select from the available providers.
        const message = 'No Active Domain Providers'
        const error = { code: '403', layer: 'app', message }

        return this.cancelProviderRegistration(error)
      }

      // Determine whether the user's domain has multiple providers enabled.
      if (activeDomainProviders.length > 1) {
        // If the user's domain has multiple providers enabled, show the provider selection dialog.
        return this.showProviderRegistrationView(domainDoc, activeDomainProviders)
      }

      // Register the user with the only provider available.

      const { providerId } = activeDomainProviders[0]

      // Store the providerId for reference by the view.
      this.providerId = providerId

      // Check if the only provider is password.
      if (providerId === 'password') {
        // Reveal the "Register Password" input to the user.
        return this.showRegisterPasswordInput()
      }

      // Register the user with a provider other than password.
      const register = this.registerAccount(providerId)

      // Reset the registration state.
      this.isRegisteringAccountWithProvider = false

      // Clear the provider selection dialog.
      this.showViewForProviderSelection = false

      return register
    },

    async signInWithProvider(providerId) {
      // Get the domain document.
      const { domainDoc } = this

      // Store the providerId for reference by the view.
      this.providerId = providerId

      // Get the selected provider.
      const providerDoc = await this.getProviderFromFirestore({ domainDoc, providerId })

      // Sign in the user with the provider.
      const signInData = {
        domainDoc,
        email: this.inputEmail,
        password: this.inputPassword,
        provider: providerDoc,
        tenantId: this.tenantId,
      }

      // Sign in the user with a provider other than password.
      return this.signIn(signInData).then((res) => {
        this.isSendingRegistration = false

        if (res.error) {
          // Inform the user of the error.
          return this.handleSignInError(res)
        }

        return res
      }).catch((error) => {
        // Inform the user of the error.
        this.handleSignInError(error)
      })
    },

    setDomainNameAndTenantId() {
      /**
       * Determine whether the deploy uses the default domain configs.
       * This asserts that an internal user must be a user of the
       * configured tenant to sign in to an environment other than
       * the default at iadops.com.
       */
      const isDefaultDomainDeploy = /default/i.test(FIREBASE_DOMAIN_NAME)
      const isInternalDomain = /adops\.com$/.test(this.inputEmail)

      if (isInternalDomain && isDefaultDomainDeploy) {
        this.domainName = FIREBASE_DOMAIN_NAME_FOR_ADOPS_TENANT
        this.tenantId = FIREBASE_TENANT_ID_FOR_ADOPS_TENANT
      }
    },

    async attemptSignIn() {
      // Set the domain name and tenant ID before getting the domain document.
      this.setDomainNameAndTenantId()

      // Check the user's domain in Firestore.
      const domainDoc = await this.$store.dispatch('auth/getDomainDocFromFirestore', this.domainName).catch((error) => error)

      if (!domainDoc) {
        return this.handleAuthError({ code: '404', layer: 'app', message: 'Domain Error' })
      }

      if (domainDoc.error) {
        return this.handleAuthError(domainDoc)
      }

      // Handle inactive domains.
      if (!domainDoc.isActive) {
        const message = 'Domain Inactive'
        const error = { code: '403', layer: 'app', message }

        return this.handleAuthError(error)
      }

      // Save the domain document for later use by the provider selection dialog.
      this.domainDoc = domainDoc

      // Determine whether the user's domain is misconfigured.
      // Check if the user's domain has any providers enabled.
      // Err if there are no providers.
      if (!Array.isArray(domainDoc.providers)) {
        const message = 'Domain Error: Providers Not Found'
        const error = { code: '403', layer: 'app', message }

        return this.handleAuthError(error)
      }

      if (domainDoc.providers.length === 0) {
        const message = 'Domain Error: No Providers Found'
        const error = { code: '403', layer: 'app', message }

        return this.handleAuthError(error)
      }

      // Check if the user's domain has multiple providers enabled.
      const domainProviders = domainDoc.providers
      const activeProviders = domainProviders.filter((provider) => provider.isActive)

      if (activeProviders.length > 1) {
        // Hide the "Submit" buttons.
        this.showButtonToSubmitEmail = false
        this.showButtonToRegisterNextStep = false

        // Show the provider selection view.
        return this.showProviderSelectionView(domainDoc, activeProviders)
      }

      if (activeProviders.length === 0) {
        // Inform the user and keep going so they can select from the available providers.
        const message = 'Domain Error: No Active Providers'
        const error = { code: '403', layer: 'app', message }

        return this.handleAuthError(error)
      }

      // By this point, we know there's only one provider, so we get its providerId.
      const { providerId } = activeProviders[0]

      // Store the providerId for reference by the view.
      this.providerId = providerId

      // Check if the only provider is password.
      if (providerId === 'password') {
        // Hide the "Submit" buttons.
        this.showButtonToSubmitEmail = false
        this.showButtonToRegisterNextStep = false

        // Reveal the password input to the user.
        return this.showPasswordInput()
      }

      // Get the selected provider.
      const providerDoc = await this.getProviderFromFirestore({ domainDoc, providerId })

      // Sign in the user with the only provider available.
      const signInData = {
        domainDoc,
        email: this.inputEmail,
        password: this.inputPassword,
        provider: providerDoc,
        tenantId: this.tenantId,
      }

      // Sign in the user with a provider other than password.
      return this.signIn(signInData).then((res) => {
        this.isSendingRegistration = false

        if (res.error) {
          // Inform the user of the error.
          return this.handleSignInError(res)
        }

        return res
      }).catch((error) => {
        // Inform the user of the error.
        this.handleSignInError(error)
      })
    },

    async signInWithPassword() {
      // Store the providerId for reference by the view.
      this.providerId = 'password'

      // Get the user's domain document.
      const { domainDoc, providerId } = this

      // Get the selected provider.
      const providerDoc = await this.getProviderFromFirestore({ domainDoc, providerId })

      // Check if the password is provided.
      if (!this.inputPassword) {
        // Alert the user that the password is required.
        return this.handleSignInError({ code: '403', layer: 'app', message: 'Password is required' })
      }

      // Configure the sign in data.
      const signInData = {
        domainDoc: this.domainDoc,
        email: this.inputEmail,
        password: this.inputPassword,
        provider: providerDoc,
        tenantId: this.tenantId,
      }

      // Sign in the user with the password provider.
      return this.signIn(signInData).then((res) => {
        this.isSendingRegistration = false

        if (res.error) {
          // Inform the user of the error.
          return this.handleSignInError(res)
        }

        return res
      }).catch((error) => {
        // Inform the user of the error.
        this.handleSignInError(error)
      })
    },
  },
}
</script>
